import React, { useState, useEffect, useCallback } from "react";

/**
 * VirtualTeachingPractice
 * -----------------------
 * Interactive activity checklist for a Teams + Canvas PD session.
 * Drop into any React app:
 *     import VirtualTeachingPractice from "./VirtualTeachingPractice";
 *     <VirtualTeachingPractice />
 *
 * - No external dependencies. All styles are namespaced (.vtp-*) and injected
 *   inline, so nothing collides with your app's CSS.
 * - Progress is saved to localStorage, so a refresh won't lose ticks.
 *   Set PERSIST = false to disable, or change STORAGE_KEY.
 * - Restyle by overriding the CSS variables in the .vtp-root block
 *   (e.g. --vtp-accent) from your own stylesheet.
 */

const STORAGE_KEY = "vtp-progress-v1";
const PERSIST = true;

const RESOURCE_SHEET_URL =
  "https://docs.google.com/document/d/1J8z4Z27k3q0wQV7OhaOdZE5J5aG8IN-cNQIHHVYOqcE";
const HELPDESK_EMAIL = "helpdesk@kingsacademy.edu.jo";

const GOAL =
  "Practice running an online class in Teams and Canvas together: create the meeting from inside Canvas, then use the Whiteboard and Breakout rooms as the host. Find a partner. You will run the activity twice and swap roles, so you each get a turn hosting.";

const BEFORE = {
  id: "before",
  title: "Before you start",
  kind: "check",
  items: [
    { id: "b1", text: "Pair up with a partner." },
    {
      id: "b2",
      text: "Both watch the how-to video together, “Create a Teams meeting inside Canvas.”",
      link: {
        url: "https://drive.google.com/file/d/1NK4GqnbS12A_JnCP7J2Jv_B_zKuw66Gn/view",
        label: "Watch the video",
      },
    },
    { id: "b3", text: "Publish your Sandbox course in Canvas." },
    { id: "b4", text: "Add your partner as a member of your Sandbox course." },
    {
      id: "b5",
      text: "Open the Teams desktop app* (Windows or Mac) and sign in with your King’s account.",
    },
    { id: "b6", text: "Grab your headphones OR move to separate spaces." },
  ],
  notes: [
    {
      label: "Why the desktop app?",
      text: "The host can only open Breakout rooms from the installed Teams app, not the browser version. Signing in there now saves a scramble later.",
    },
    {
      label: "Why headphones?",
      text: "Two live calls in the same space will echo loudly without them.",
    },
  ],
};

const ROUND1 = {
  id: "round1",
  title: "Round 1 — Partner A hosts",
  tag: "Host = Partner A · Guest = Partner B",
  kind: "steps",
  items: [
    {
      id: "r1-1",
      lead: "A creates the meeting.",
      text: "In your Sandbox course, create a Teams meeting from inside Canvas, set to start now, and post the link on a page or announcement.",
    },
    {
      id: "r1-2",
      lead: "Check B can get in.",
      text: "Before you split up, have B open A’s Sandbox course and confirm they can see it and the meeting link. If they can’t, they may need to accept the course invite first.",
    },
    {
      id: "r1-3",
      lead: "Split up.",
      text: "Move to separate spaces so you are each on your own device.",
    },
    {
      id: "r1-4",
      lead: "B joins A’s meeting",
      text: "from the link in A’s Canvas course.",
    },
    {
      id: "r1-5",
      lead: "A runs the Whiteboard.",
      text: "Open the Whiteboard and switch between the edit modes — only the presenter can edit vs everyone can edit. Have B try to write in each mode so you both see the difference.",
    },
    {
      id: "r1-6",
      lead: "A runs a Breakout room.",
      text: "From the Teams desktop app, open Breakout rooms, create one room, assign B, join the room yourself, send a broadcast message, then bring everyone back and close the room.",
    },
  ],
  notes: [
    {
      label: "Heads up:",
      text: "Breakout rooms only appear when the host joined from the Teams desktop app. If you joined in a browser, the button won’t be there.",
      warn: true,
    },
  ],
};

const ROUND2 = {
  id: "round2",
  title: "Round 2 — Swap roles",
  tag: "Host = Partner B · Guest = Partner A",
  kind: "steps",
  intro:
    "Now Partner B hosts and Partner A is the guest. Run the same steps in B’s Sandbox course and B’s meeting, so you both get a turn driving the Whiteboard and Breakout rooms.",
  items: [
    {
      id: "r2-1",
      lead: "B creates the meeting.",
      text: "In B’s Sandbox course, create a Teams meeting from inside Canvas, set to start now, and post the link.",
    },
    {
      id: "r2-2",
      lead: "Check A can get in.",
      text: "Before you split up, have A open B’s Sandbox course and confirm they can see it and the meeting link.",
    },
    { id: "r2-3", lead: "Split up.", text: "Head back to separate spaces." },
    {
      id: "r2-4",
      lead: "A joins B’s meeting",
      text: "from the link in B’s Canvas course.",
    },
    {
      id: "r2-5",
      lead: "B runs the Whiteboard.",
      text: "Switch between presenter-only and everyone-can-edit modes; have A test each.",
    },
    {
      id: "r2-6",
      lead: "B runs a Breakout room.",
      text: "From the desktop app, open one room, assign A, hop in, broadcast a message, then close it.",
    },
  ],
};

const EXPLORE = {
  id: "explore",
  title: "If you finish early — explore",
  kind: "check",
  optional: true,
  intro:
    "Stay in your meeting and try a few more engagement tools, starting with what’s already built in:",
  items: [
    {
      id: "e1",
      lead: "Polls",
      text: "— launch a quick poll in the meeting (powered by Forms).",
    },
    {
      id: "e2",
      lead: "Reactions & raise hand",
      text: "— the simplest way to keep students signaling in.",
    },
    {
      id: "e3",
      lead: "Collaborative notes",
      text: "— shared live notes inside the meeting.",
    },
    {
      id: "e4",
      lead: "Record & transcript",
      text: "— record the session and grab the transcript (the same input the King’s Transcriber uses for async students).",
    },
    {
      id: "e5",
      lead: "Canvas Studio",
      text: "— back in Canvas, record a one-minute video and embed a question in it.",
    },
  ],
  footer:
    "Want more? The resource sheet has Padlet, Kahoot, GimKit, Canva, and Jotboard to play with.",
};

const REQUIRED_IDS = [...BEFORE.items, ...ROUND1.items, ...ROUND2.items].map(
  (i) => i.id
);

const CSS = `
.vtp-root {
  /* layout: single centered column of stacked section cards */
  --vtp-bg: #f6f5f3;
  --vtp-surface: #ffffff;
  --vtp-fg: #1d2430;
  --vtp-muted: #5c6672;
  --vtp-accent: #4f46e5;
  --vtp-accent-soft: #ecebfb;
  --vtp-border: #e4e2dd;
  --vtp-note-bg: #f1f0fa;
  --vtp-warn-bg: #fdf3e3;
  --vtp-warn-fg: #7a4b12;
  --vtp-warn-border: #eecb8f;
  --vtp-done: #1a7f5a;

  box-sizing: border-box;
  max-width: 760px;
  margin: 0 auto;
  padding: clamp(16px, 4vw, 32px);
  background: var(--vtp-bg);
  color: var(--vtp-fg);
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 15px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
}
.vtp-root *, .vtp-root *::before, .vtp-root *::after { box-sizing: border-box; }

@media (prefers-color-scheme: dark) {
  .vtp-root:not([data-vtp-theme="light"]) {
    --vtp-bg: #15171c;
    --vtp-surface: #1f232b;
    --vtp-fg: #e8eaed;
    --vtp-muted: #9aa3af;
    --vtp-accent: #8b8cf6;
    --vtp-accent-soft: #2a2b45;
    --vtp-border: #333a44;
    --vtp-note-bg: #24263a;
    --vtp-warn-bg: #352a16;
    --vtp-warn-fg: #e9c891;
    --vtp-warn-border: #5e4a22;
    --vtp-done: #4cc79a;
    color-scheme: dark;
  }
}
.vtp-root[data-vtp-theme="dark"] {
  --vtp-bg: #15171c;
  --vtp-surface: #1f232b;
  --vtp-fg: #e8eaed;
  --vtp-muted: #9aa3af;
  --vtp-accent: #8b8cf6;
  --vtp-accent-soft: #2a2b45;
  --vtp-border: #333a44;
  --vtp-note-bg: #24263a;
  --vtp-warn-bg: #352a16;
  --vtp-warn-fg: #e9c891;
  --vtp-warn-border: #5e4a22;
  --vtp-done: #4cc79a;
  color-scheme: dark;
}

.vtp-header { margin-bottom: 20px; }
.vtp-eyebrow {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--vtp-accent);
  font-weight: 700;
  margin: 0 0 6px;
}
.vtp-title {
  font-size: clamp(22px, 5vw, 30px);
  line-height: 1.15;
  font-weight: 800;
  margin: 0 0 6px;
  text-wrap: balance;
}
.vtp-subtitle { color: var(--vtp-muted); margin: 0 0 16px; font-size: 15px; }
.vtp-goal {
  background: var(--vtp-surface);
  border: 1px solid var(--vtp-border);
  border-left: 4px solid var(--vtp-accent);
  border-radius: 10px;
  padding: 14px 16px;
  margin: 0 0 18px;
}

/* progress */
.vtp-progress {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--vtp-bg);
  padding: 10px 0 12px;
  margin-bottom: 8px;
}
.vtp-progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.vtp-progress-label { font-weight: 700; font-size: 14px; }
.vtp-progress-label .vtp-done-flag { color: var(--vtp-done); }
.vtp-reset {
  appearance: none;
  border: 1px solid var(--vtp-border);
  background: var(--vtp-surface);
  color: var(--vtp-muted);
  font: inherit;
  font-size: 13px;
  padding: 5px 12px;
  border-radius: 999px;
  cursor: pointer;
}
.vtp-reset:hover { color: var(--vtp-fg); border-color: var(--vtp-muted); }
.vtp-track {
  height: 8px;
  border-radius: 999px;
  background: var(--vtp-border);
  overflow: hidden;
}
.vtp-fill {
  height: 100%;
  border-radius: 999px;
  background: var(--vtp-accent);
  transition: width 0.3s ease;
}

/* sections */
.vtp-section {
  background: var(--vtp-surface);
  border: 1px solid var(--vtp-border);
  border-radius: 12px;
  padding: 18px;
  margin-top: 14px;
}
.vtp-section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.vtp-section-title { font-size: 18px; font-weight: 800; margin: 0; }
.vtp-section-tag {
  font-size: 12px;
  font-weight: 700;
  color: var(--vtp-accent);
  background: var(--vtp-accent-soft);
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
}
.vtp-optional-tag {
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--vtp-muted);
  font-weight: 700;
}
.vtp-intro { color: var(--vtp-muted); margin: 6px 0 12px; }

/* items */
.vtp-list { list-style: none; margin: 10px 0 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.vtp-item { display: flex; gap: 12px; align-items: flex-start; padding: 8px; border-radius: 8px; }
.vtp-item:hover { background: var(--vtp-accent-soft); }
.vtp-item label { display: flex; gap: 12px; align-items: flex-start; cursor: pointer; width: 100%; }
.vtp-check {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin: 1px 0 0;
  accent-color: var(--vtp-accent);
  cursor: pointer;
}
.vtp-step-num {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: var(--vtp-accent-soft);
  color: var(--vtp-accent);
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.vtp-item-text { min-width: 0; }
.vtp-item-text strong { font-weight: 700; }
.vtp-item.done .vtp-item-text { color: var(--vtp-muted); }
.vtp-item.done .vtp-step-num { background: var(--vtp-done); color: #fff; }
.vtp-inline-link {
  color: var(--vtp-accent);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
}
.vtp-inline-link:hover { text-decoration-thickness: 2px; }

/* notes */
.vtp-note {
  margin-top: 12px;
  padding: 11px 14px;
  border-radius: 9px;
  background: var(--vtp-note-bg);
  font-size: 14px;
}
.vtp-note strong { font-weight: 700; }
.vtp-note.warn {
  background: var(--vtp-warn-bg);
  color: var(--vtp-warn-fg);
  border: 1px solid var(--vtp-warn-border);
}
.vtp-note.warn strong { color: inherit; }

.vtp-footer-note { margin-top: 14px; color: var(--vtp-muted); font-size: 14px; }

/* help */
.vtp-help { margin-top: 14px; display: flex; flex-direction: column; gap: 8px; font-size: 14px; }
.vtp-help a { color: var(--vtp-accent); font-weight: 600; text-decoration: none; }
.vtp-help a:hover { text-decoration: underline; }
.vtp-help .vtp-label { font-weight: 700; }

@media (max-width: 440px) {
  .vtp-section { padding: 14px; }
}
@media (prefers-reduced-motion: reduce) {
  .vtp-fill { transition: none; }
}
`;

function Item({ item, done, onToggle, numbered, index }) {
  return (
    <li className={"vtp-item" + (done ? " done" : "")}>
      <label>
        <input
          className="vtp-check"
          type="checkbox"
          checked={done}
          onChange={() => onToggle(item.id)}
        />
        {numbered ? <span className="vtp-step-num" aria-hidden="true">{index}</span> : null}
        <span className="vtp-item-text">
          {item.lead ? <strong>{item.lead}</strong> : null}
          {item.lead ? " " : null}
          {item.text}
          {item.link ? (
            <>
              {" "}
              <a
                className="vtp-inline-link"
                href={item.link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {item.link.label}
              </a>
            </>
          ) : null}
        </span>
      </label>
    </li>
  );
}

function Section({ section, checked, onToggle }) {
  const numbered = section.kind === "steps";
  return (
    <section className="vtp-section">
      <div className="vtp-section-head">
        <h2 className="vtp-section-title">{section.title}</h2>
        {section.tag ? <span className="vtp-section-tag">{section.tag}</span> : null}
        {section.optional ? <span className="vtp-optional-tag">Optional</span> : null}
      </div>
      {section.intro ? <p className="vtp-intro">{section.intro}</p> : null}
      <ul className="vtp-list">
        {section.items.map((item, i) => (
          <Item
            key={item.id}
            item={item}
            index={i + 1}
            numbered={numbered}
            done={checked.has(item.id)}
            onToggle={onToggle}
          />
        ))}
      </ul>
      {(section.notes || []).map((n, i) => (
        <div key={i} className={"vtp-note" + (n.warn ? " warn" : "")}>
          <strong>{n.label}</strong> {n.text}
        </div>
      ))}
      {section.footer ? <p className="vtp-footer-note">{section.footer}</p> : null}
    </section>
  );
}

export default function VirtualTeachingPractice() {
  const [checked, setChecked] = useState(() => {
    if (!PERSIST) return new Set();
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch (e) {
      return new Set();
    }
  });

  useEffect(() => {
    if (!PERSIST) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...checked]));
    } catch (e) {
      /* storage unavailable — progress just won't persist */
    }
  }, [checked]);

  const toggle = useCallback((id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const reset = useCallback(() => setChecked(new Set()), []);

  const doneCount = REQUIRED_IDS.filter((id) => checked.has(id)).length;
  const total = REQUIRED_IDS.length;
  const percent = total ? Math.round((doneCount / total) * 100) : 0;
  const complete = doneCount === total;

  return (
    <div className="vtp-root">
      <style>{CSS}</style>

      <header className="vtp-header">
        <p className="vtp-eyebrow">PD Activity</p>
        <h1 className="vtp-title">Virtual Teaching Practice</h1>
        <p className="vtp-subtitle">
          Teams + Canvas hands-on: meetings, Whiteboard &amp; Breakout rooms
        </p>
        <div className="vtp-goal">{GOAL}</div>
      </header>

      <div className="vtp-progress">
        <div className="vtp-progress-row">
          <span className="vtp-progress-label">
            {complete ? (
              <span className="vtp-done-flag">{"All activities complete ✓"}</span>
            ) : (
              <span>
                {doneCount} of {total} activities done
              </span>
            )}
          </span>
          <button className="vtp-reset" type="button" onClick={reset}>
            Reset
          </button>
        </div>
        <div
          className="vtp-track"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="vtp-fill" style={{ width: percent + "%" }} />
        </div>
      </div>

      <Section section={BEFORE} checked={checked} onToggle={toggle} />
      <Section section={ROUND1} checked={checked} onToggle={toggle} />
      <Section section={ROUND2} checked={checked} onToggle={toggle} />
      <Section section={EXPLORE} checked={checked} onToggle={toggle} />

      <section className="vtp-section">
        <div className="vtp-section-head">
          <h2 className="vtp-section-title">Need help?</h2>
        </div>
        <div className="vtp-help">
          <div>
            <span className="vtp-label">Tech issues:</span>{" "}
            <a href={"mailto:" + HELPDESK_EMAIL}>{HELPDESK_EMAIL}</a>
          </div>
          <div>
            <span className="vtp-label">Resource sheet:</span>{" "}
            <a href={RESOURCE_SHEET_URL} target="_blank" rel="noopener noreferrer">
              Virtual Teaching Resources
            </a>{" "}
            <span style={{ color: "var(--vtp-muted)" }}>
              {"— the how-to video, quick guides, and the full tool list."}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}